import { useCallback, useEffect } from "react"

type EventReviewHotKeysConfig = {
  isActive: boolean
  onNext: () => void
  onPrevious: () => void
  onPlayThrough?: () => void
  onJumpToCurrent?: () => void
}

const KEY_BINDINGS = {
  next: ["]"],
  previous: ["["],
  play: ["p"],
  jump: ["j"],
}

function isTypingTarget(element: Element | null): boolean {
  if (!element) return false
  return (
    element.tagName === "INPUT" ||
    element.tagName === "TEXTAREA" ||
    element.getAttribute("contenteditable") === "true"
  )
}

export const useEventReviewHotKeys = ({
  isActive,
  onNext,
  onPrevious,
  onPlayThrough,
  onJumpToCurrent,
}: EventReviewHotKeysConfig) => {
  const handler = useCallback((event: KeyboardEvent) => {
    if (!isActive || isTypingTarget(document.activeElement)) return

    const key = event.key

    if (KEY_BINDINGS.next.includes(key)) {
      event.preventDefault()
      event.stopPropagation()
      onNext()
      return
    }

    if (KEY_BINDINGS.previous.includes(key)) {
      event.preventDefault()
      event.stopPropagation()
      onPrevious()
      return
    }

    if (onPlayThrough && KEY_BINDINGS.play.includes(key)) {
      event.preventDefault()
      event.stopPropagation()
      onPlayThrough()
      return
    }

    if (onJumpToCurrent && KEY_BINDINGS.jump.includes(key)) {
      event.preventDefault()
      event.stopPropagation()
      onJumpToCurrent()
      return
    }
  }, [isActive, onNext, onPrevious, onPlayThrough, onJumpToCurrent])

  useEffect(() => {
    if (!isActive) return

    document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [handler, isActive])
}
